<template>
  <v-dialog v-model="dialog" persistent max-width="600">
    <ConfirmationDialog2 ref="confirmationDialog" />
    <v-card dark>
      <v-toolbar dark color="primary">
        <v-toolbar-title>Edit Monitoring Location</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click.native="close()" dark>
          <v-icon>close</v-icon>
        </v-btn>
      </v-toolbar>
    </v-card>
    <v-card>
      <LocationForm v-if="dialog" ref="form" />
      <v-card-text>
        <strong>Note:</strong> Data sources can be edited in the `Data Sources` tab.
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-btn color="error" outline :loading="loading.remove" @click="remove()">
          <v-icon>delete_forever</v-icon>
          Remove
        </v-btn>
        <v-spacer />
        <v-btn color="success" :disabled="loading.remove" :loading="loading.submit" @click="submit()">
          <v-icon class="mr-2">done</v-icon>
          Save
        </v-btn>
        <v-btn depressed :disabled="loading.submit || loading.remove" @click="close()">
          Cancel
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import {
    createNamespacedHelpers,
  } from 'vuex'

  const app = createNamespacedHelpers('app')
  const monitoringFeatures = createNamespacedHelpers('monitoringfeature')
  const dataSources = createNamespacedHelpers('datasource')

  import LocationForm from './LocationForm'

  export default {
    components: {
      LocationForm,
    },
    data: () => ({
      dialog: false,
      loading: {
        submit: false,
        remove: false,
      },
      monitoringFeature: null,
    }),
    computed: {
      dataSourceLength() {
        return (this.monitoringFeature && this.monitoringFeature.dataSourceLength) || 0
      },
      isNonEmptyMonitoringFeature() {
        return this.dataSourceLength > 0
      },
    },
    methods: {
      ...monitoringFeatures.mapActions(['updateMonitoringFeature', 'removeMonitoringFeature']),
      ...dataSources.mapActions(['setDSMonitoringFeatureId']),
      ...app.mapActions(['setSnackbar']),
      close() {
        this.dialog = false
        this.loading.submit = false
        this.loading.remove = false
      },
      async show(monitoringFeature) {
        this.dialog = true
        this.monitoringFeature = monitoringFeature

        await this.$nextTick()
        this.$refs.form.setForm(monitoringFeature)
      },
      async submit() {
        if (!this.$refs.form.validate()) return

        this.loading.submit = true
        try {
          let payload = this.$refs.form.getForm()
          await this.updateMonitoringFeature({
            monitoringFeature: payload,
          })

          let promises = payload.dataTypes.map(dt => this.setDSMonitoringFeatureId(dt))
          await Promise.all(promises)

          this.setSnackbar({
            text: 'Location updated',
            color: 'success',
            icon: 'done',
            timeout: 10000,
          })

          this.close()
        } catch (error) {
          this.loading.submit = false
          this.setSnackbar({
            text: error,
            color: 'error',
            icon: 'error',
            timeout: 10000,
          })
        }
      },
      async remove() {
        if (this.isNonEmptyMonitoringFeature) {
          await this.showCannotDeleteNonEmptyLocationMessage()
        } else {
          await this.confirmRemove()
        }
      },
      async confirmRemove() {
        this.loading.remove = true
        const params = {
          title: 'Remove monitoring location?',
          text: `<div>This will remove the location <strong>${this.monitoringFeature.primaryName}</strong>.</div><br/> <div><strong>Note:</strong> The location can only be removed if no data sources are assigned to it.</div>`,
          confirmText: 'Remove',
          cancelText: 'Cancel',
          color: 'error',
          icon: 'delete_forever',
        }

        let confirmed = await this.$refs.confirmationDialog.confirm(params)
        if (!confirmed) {
          this.loading.remove = false
          return
        }

        try {
          await this.removeMonitoringFeature(this.monitoringFeature)
          this.setSnackbar({
            text: 'Location removed',
            color: 'info',
            icon: 'done',
            timeout: 10000,
          })

          this.close()
        } catch (error) {
          this.loading.remove = false
          this.setSnackbar({
            text: error,
            color: 'error',
            icon: 'error',
            timeout: 10000,
          })
        }
      },
      async showCannotDeleteNonEmptyLocationMessage() {
        const pluralChar = this.dataSourceLength <= 1 ? '' : 's'
        const dataSourceWord = `data source${pluralChar}`
        const params = {
          title: 'Monitoring Location with data sources cannot be removed.',
          text: `<div>The location you are trying to delete contains ${this.dataSourceLength} ${dataSourceWord}. Only empty locations can be deleted. Please unassign, or reassign ${dataSourceWord} to other locations and try deleting again.</div>`,
          confirmText: '',
          cancelText: 'Cancel',
          color: '',
          icon: '',
        }

        await this.$refs.confirmationDialog.confirm(params)
        return false
      },
    },

  }
</script>